import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faList,
  faListCheck,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { format, differenceInCalendarDays, isValid } from 'date-fns';
import './TasksPage.css';
import TaskService from '../AppSettings/services/TaskService';
import MatterService from '../AppSettings/services/MatterService';
import EmployeeService from '../AppSettings/services/EmployeeService';
import useActiveEmployeeId from '../AppSettings/hooks/useActiveEmployeeId';
import useCurrentEmployee from '../AppSettings/hooks/useCurrentEmployee';
import { isEngineeringEnvironment } from '../AppSettings/environmentConfig';
import { parseLocalDate } from '../helpers/dateHelpers';
import { TaskByEmployee } from '../Models/Tasks/TaskModels';
import Tabs from '@frontend/Components/Tabs';
import SearchBar from '@frontend/Components/Inputs/SearchBar/SearchBar';
import FilterDropdown from '@frontend/Components/FilterDropdown';
import Table, { getDensityClass } from '@frontend/Components/Table/Table';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';
import CreateTasksPage from './Tasks/CreateTasks/CreateTasksPage';
import CreateButton from '@frontend/Components/CreateButton/CreateButton';
import PageHeader from '@frontend/Components/Titles/PageHeader/PageHeader';
import ActionItemDetailsPanel from './Tasks/Components/ActionItemDetailsPanel/ActionItemDetailsPanel';

type UrgencyLevel = 'Urgent' | 'High' | 'Medium' | 'Low' | 'all';

type SortKey =
  | 'title'
  | 'project'
  | 'client'
  | 'leader'
  | 'status'
  | 'scope'
  | 'duedate'
  | 'priority';
type SortDir = 'asc' | 'desc' | null;

const PRIORITY_RANK: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

const TasksPage = () => {
  const employeeId = useActiveEmployeeId();
  const currentEmployee = useCurrentEmployee();
  const permishId = currentEmployee?.permish_id ?? null;
  const permishIdNum = typeof permishId === 'string' ? parseInt(permishId, 10) : permishId;
  const canViewTeamTasks = permishIdNum === 1 || permishIdNum === 2;
  const [searchParams, setSearchParams] = useSearchParams();
  const _location = useLocation();
  const queryClient = useQueryClient();

  // State for create modal popup
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Initialize state from URL params
  const tabFromUrl = searchParams.get('tab') as 'my-tasks' | 'teams-tasks' | 'tasks-assigned' | null;
  const urgencyFromUrl = searchParams.get('urgency') as UrgencyLevel | null;
  const taskIdFromUrl = searchParams.get('taskId');

  // State management — initialized from URL params so filters survive navigation
  const [activeTab, setActiveTab] = useState<'my-tasks' | 'teams-tasks' | 'tasks-assigned'>(
    tabFromUrl || 'my-tasks'
  );
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const [pageSize, setPageSize] = useState(7);
  const [pageLimit, setPageLimit] = useState(1);
  const [selectedTask, setSelectedTask] = useState<TaskByEmployee | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);

  // Filter states — initialized from URL params
  const [matterFilter, setMatterFilter] = useState(searchParams.get('matter') || 'all');
  const [scopeFilter, setScopeFilter] = useState(searchParams.get('scope') || 'all');
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || 'all');
  const [urgencyFilter, _setUrgencyFilter] = useState<UrgencyLevel>(
    urgencyFromUrl || 'all'
  );
  const [countryFilter, setCountryFilter] = useState(searchParams.get('country') || 'all');
  const [statusFilter, setStatusFilter] = useState<string | null>(searchParams.get('status'));
  const [leaderFilter, setLeaderFilter] = useState(searchParams.get('leader') || 'all');
  const [sortBy, setSortBy] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  
  // Track if we've initialized from URL to avoid syncing on mount
  const isInitialized = useRef(false);

  // Fetch tasks
  const { data: allTasks = [] } = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => TaskService.getAllTasks(),
  });

  // Fetch matters for filter options and country data
  const { data: allMatters = [] } = useQuery({
    queryKey: ['matters', 'all'],
    queryFn: () => MatterService.getProjectMatters(),
  });

  // Fetch employees for assigned leader info
  const { data: employees = [] } = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: () => EmployeeService.getEmployees(),
  });

  // Get employee name by ID (needed for search + leader filter before applyFilters)
  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const emp of employees as any[]) {
      const id = emp?.EmployeeID;
      if (!id) continue;
      const name = `${emp?.FirstName || ''} ${emp?.LastName || ''}`.trim();
      map.set(id, name || '-');
    }
    return map;
  }, [employees]);

  // Filter tasks based on active tab
  const tasks = useMemo(() => {
    if (activeTab === 'my-tasks' && employeeId) {
      return allTasks.filter((task) => task.assignedto === employeeId);
    }
    if (activeTab === 'tasks-assigned' && employeeId) {
      return allTasks.filter((task) => task.createdby === employeeId);
    }
    return allTasks;
  }, [allTasks, activeTab, employeeId]);

  // Apply filters and search (excluding status filter for summary counts)
  const applyFilters = useCallback((taskList: TaskByEmployee[], includeStatusFilter: boolean = true) => {
    let filtered = [...taskList];

    // Search filter — title, matter/project, assignee id, or employee name
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter((task) => {
        const assigneeName = task.assignedto
          ? (employeeNameById.get(task.assignedto) || '').toLowerCase()
          : '';
        return (
          task.task_title?.toLowerCase().includes(lower) ||
          task.mattername?.toLowerCase().includes(lower) ||
          task.assignedto?.toLowerCase().includes(lower) ||
          assigneeName.includes(lower)
        );
      });
    }

    // Matter filter
    if (matterFilter !== 'all') {
      filtered = filtered.filter((task) => task.matterid === matterFilter);
    }

    // Assigned leader filter
    if (leaderFilter !== 'all') {
      filtered = filtered.filter((task) => task.assignedto === leaderFilter);
    }

    // Scope filter (using task_type as scope for now)
    if (scopeFilter !== 'all') {
      filtered = filtered.filter((task) => task.task_type === scopeFilter);
    }

    // Priority filter: use task.priority when available, else derive from due date
    if (priorityFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter((task) => {
        const taskPriority = task.priority
          ? task.priority.toLowerCase()
          : task.duedate
            ? (() => {
                const dueDate = parseLocalDate(task.duedate);
                if (!isValid(dueDate)) return 'none';
                const daysDiff = differenceInCalendarDays(dueDate, now);
                if (daysDiff < 0 || daysDiff === 0) return 'urgent';
                if (daysDiff <= 1) return 'high';
                if (daysDiff <= 7) return 'medium';
                return 'low';
              })()
            : 'none';
        return taskPriority === priorityFilter;
      });
    }

    // Urgency filter (matches ActionItemsWidget logic)
    if (urgencyFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter((task) => {
        // Only filter pending or in-progress tasks
        const status = (task.status || '').toLowerCase();
        if (status !== 'pending' && status !== 'in progress') {
          return false;
        }

        if (!task.duedate) {
          return urgencyFilter === 'Low'; // Tasks without due dates are considered Low
        }

        const dueDate = parseLocalDate(task.duedate);
        if (!isValid(dueDate)) {
          return false;
        }

        const daysUntilDue = differenceInCalendarDays(dueDate, now);

        // Match ActionItemsWidget urgency logic
        if (urgencyFilter === 'Urgent') {
          return daysUntilDue < 0 || daysUntilDue === 0; // Overdue or due today
        } else if (urgencyFilter === 'High') {
          return daysUntilDue > 0 && daysUntilDue <= 1; // Due tomorrow or next day
        } else if (urgencyFilter === 'Medium') {
          return daysUntilDue > 1 && daysUntilDue <= 7; // Due within a week
        } else if (urgencyFilter === 'Low') {
          return daysUntilDue > 7; // Due later
        }

        return true;
      });
    }

    // Country filter (via matter)
    if (countryFilter !== 'all') {
      const matterCountryMap = new Map<string, string>();
      allMatters.forEach((matter: any) => {
        if (matter.matterid && matter.country) {
          matterCountryMap.set(matter.matterid, matter.country);
        }
      });
      
      filtered = filtered.filter((task) => {
        if (!task.matterid) return false;
        const matterCountry = matterCountryMap.get(task.matterid);
        return matterCountry === countryFilter;
      });
    }

    // Status filter (from summary cards) - only apply if includeStatusFilter is true
    // Default (null): show only Pending + In Progress. 'Total': show all. Others: filter to that status.
    if (includeStatusFilter) {
      if (statusFilter === null) {
        // Default view: hide completed; show only Pending and In Progress
        filtered = filtered.filter(
          (task) => task.status === 'Pending' || task.status === 'In Progress'
        );
      } else if (statusFilter === 'Total') {
        // User clicked "Total Items": show all tasks (no status filter)
        // (no additional filter)
      } else if (statusFilter === 'In Progress') {
        filtered = filtered.filter((task) => task.status === 'In Progress');
      } else if (statusFilter === 'Completed') {
        filtered = filtered.filter((task) => task.status === 'Completed');
      } else if (statusFilter === 'Overdue') {
        const now = new Date();
        filtered = filtered.filter((task) => {
          if (!task.duedate) return false;
          const dueDate = parseLocalDate(task.duedate);
          if (!isValid(dueDate)) return false;
          // Calendar-day overdue (not timestamp < now, which marks "due today" as overdue)
          return differenceInCalendarDays(dueDate, now) < 0 && task.status !== 'Completed';
        });
      }
    }

    return filtered;
  }, [searchTerm, matterFilter, leaderFilter, scopeFilter, priorityFilter, urgencyFilter, countryFilter, statusFilter, allMatters, employeeNameById]);

  // Filtered tasks for table (includes status filter)
  const filteredTasks = useMemo(() => {
    return applyFilters(tasks, true);
  }, [tasks, applyFilters]);

  // Filtered tasks for summary counts (excludes status filter)
  const filteredTasksForSummary = useMemo(() => {
    return applyFilters(tasks, false);
  }, [tasks, applyFilters]);

  useEffect(() => {
    const newLimit = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
    setPageLimit(newLimit);
    setPage(prev => (prev > newLimit ? newLimit : prev));
  }, [filteredTasks.length, pageSize]);

  // Mark as initialized after first render
  useEffect(() => {
    isInitialized.current = true;
  }, []);

  // Only permission levels 1 and 2 can use the tenant-wide team tasks view.
  useEffect(() => {
    if (!canViewTeamTasks && activeTab === 'teams-tasks') {
      setActiveTab('my-tasks');
    }
  }, [canViewTeamTasks, activeTab]);

  // Handle taskId from URL params to open details panel
  useEffect(() => {
    if (taskIdFromUrl && allTasks.length > 0) {
      const task = allTasks.find((t) => t.taskid === taskIdFromUrl);
      if (task) {
        setSelectedTask(task);
        setIsDetailsPanelOpen(true);
      }
    }
  }, [taskIdFromUrl, allTasks]);

  // Sync all filter/page state to URL params so filters persist across navigation
  useEffect(() => {
    if (!isInitialized.current) return;

    const params = new URLSearchParams();
    if (activeTab !== 'my-tasks') params.set('tab', activeTab);
    if (urgencyFilter !== 'all') params.set('urgency', urgencyFilter);
    if (matterFilter !== 'all') params.set('matter', matterFilter);
    if (scopeFilter !== 'all') params.set('scope', scopeFilter);
    if (priorityFilter !== 'all') params.set('priority', priorityFilter);
    if (countryFilter !== 'all') params.set('country', countryFilter);
    if (leaderFilter !== 'all') params.set('leader', leaderFilter);
    if (statusFilter !== null) params.set('status', statusFilter);
    if (searchTerm) params.set('search', searchTerm);
    if (page > 1) params.set('page', String(page));

    const taskId = searchParams.get('taskId');
    if (taskId) params.set('taskId', taskId);

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, urgencyFilter, matterFilter, scopeFilter, priorityFilter, countryFilter, leaderFilter, statusFilter, searchTerm, page, searchParams, setSearchParams]);


  // Calculate summary counts (using filteredTasksForSummary to exclude status filter)
  const summaryCounts = useMemo(() => {
    const total = filteredTasksForSummary.length;
    const inProgress = filteredTasksForSummary.filter((task) => task.status === 'In Progress').length;
    const overdue = filteredTasksForSummary.filter((task) => {
      if (!task.duedate) return false;
      const dueDate = parseLocalDate(task.duedate);
      if (!isValid(dueDate)) return false;
      return differenceInCalendarDays(dueDate, new Date()) < 0 && task.status !== 'Completed';
    }).length;
    const completed = filteredTasksForSummary.filter((task) => task.status === 'Completed').length;
    return { total, inProgress, overdue, completed };
  }, [filteredTasksForSummary]);

  // Default work queue hides Completed. When filters leave only non-active matches,
  // surface that mismatch so Total: N + empty table is not a dead end.
  const hasHiddenWorkQueueMatches =
    statusFilter === null &&
    filteredTasks.length === 0 &&
    filteredTasksForSummary.length > 0;


  // Filter options
  const filterOptions = useMemo(() => {
    // Filter matters - remove empty values and labels
    const uniqueMatters = Array.from(
      new Set(tasks.map((task) => task.matterid).filter(Boolean))
    )
      .map((matterId) => {
        const matter = tasks.find((t) => t.matterid === matterId);
        const matterName = matter?.mattername || '';
        // Include ID in label with bold ID: <strong>ID</strong>: Name or just <strong>ID</strong> if no name
        const label = matterName 
          ? (
              <>
                <strong>{matterId}</strong>: {matterName}
              </>
            )
          : <strong>{matterId}</strong>;
        return { value: matterId, label };
      })
      .filter((option) => 
        Boolean(option.value) && Boolean(option.label)
      ) as Array<{ value: string; label: string | React.ReactNode }>; // Filter out empty values/labels

    // Filter scopes - remove empty values and labels
    const uniqueScopes = Array.from(
      new Set(tasks.map((task) => task.task_type).filter(Boolean))
    )
      .map((scope) => ({ value: scope, label: scope }))
      .filter((option): option is { value: string; label: string } => 
        Boolean(option.value) && Boolean(option.label)
      ); // Filter out empty values/labels with type guard

    // Filter countries - get unique countries from matters
    const uniqueCountries = Array.from(
      new Set(
        allMatters
          .map((matter: any) => matter.country)
          .filter((country): country is string => Boolean(country))
      )
    )
      .sort()
      .map((country) => ({ value: country, label: country }));

    // Assigned leaders present on tasks in the current tab
    const uniqueLeaders = Array.from(
      new Set(tasks.map((task) => task.assignedto).filter(Boolean) as string[])
    )
      .map((id) => ({
        value: id,
        label: employeeNameById.get(id) || id,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      );

    // Keep the active selection visible even if that assignee isn't on this tab's tasks
    if (
      leaderFilter !== 'all' &&
      !uniqueLeaders.some((leader) => leader.value === leaderFilter)
    ) {
      uniqueLeaders.unshift({
        value: leaderFilter,
        label: employeeNameById.get(leaderFilter) || leaderFilter,
      });
    }

    return {
      matters: [{ value: 'all', label: isEngineeringEnvironment ? 'All Projects' : 'All Matters' }, ...uniqueMatters],
      scopes: [{ value: 'all', label: 'All Scopes' }, ...uniqueScopes],
      priorities: [
        { value: 'all', label: 'All Priorities' },
        { value: 'urgent', label: 'Urgent' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
        { value: 'none', label: 'None' },
      ],
      countries: [{ value: 'all', label: 'All Areas' }, ...uniqueCountries],
      leaders: [{ value: 'all', label: 'All Leaders' }, ...uniqueLeaders],
    };
  }, [tasks, allMatters, employeeNameById, leaderFilter]);

  // Get client information by matter ID
  const clientInfoByMatterId = useMemo(() => {
    const map = new Map<string, { clientid?: string; clientname: string }>();
    for (const matter of allMatters as any[]) {
      if (matter?.matterid) {
        map.set(matter.matterid, {
          clientid: matter.clientid,
          clientname: matter.clientname || matter.client_name || '',
        });
      }
    }
    return map;
  }, [allMatters]);

  // Format due date with countdown (parse as local calendar date — avoids UTC off-by-one)
  const formatDueDate = (dueDate: string | null | undefined) => {
    if (!dueDate) return { date: '-', status: '', daysLeft: null };
    const date = parseLocalDate(dueDate);

    // Check if date is valid before formatting
    if (!isValid(date)) {
      return { date: 'Invalid Date', status: '', daysLeft: null };
    }

    const now = new Date();
    const daysDiff = differenceInCalendarDays(date, now);
    const dateStr = format(date, 'MMM d, yyyy');
    let status = '';
    let daysLeft: number | null = daysDiff;

    if (daysDiff < 0) {
      status = 'overdue';
      daysLeft = Math.abs(daysDiff);
    } else if (daysDiff === 0) {
      status = 'today';
      daysLeft = 0;
    } else if (daysDiff <= 7) {
      status = 'urgent';
      daysLeft = daysDiff;
    } else {
      status = 'upcoming';
      daysLeft = daysDiff;
    }

    return { date: dateStr, status, daysLeft };
  };

  // Get priority: use task.priority when available, otherwise derive from due date
  const getPriority = useCallback((task: TaskByEmployee): string => {
    if (task.priority) return task.priority;
    if (!task.duedate) return 'None';
    const now = new Date();
    const due = parseLocalDate(task.duedate);
    if (!isValid(due)) return 'None';
    const daysDiff = differenceInCalendarDays(due, now);
    if (daysDiff < 0 || daysDiff === 0) return 'Urgent';
    if (daysDiff <= 1) return 'High';
    if (daysDiff <= 7) return 'Medium';
    return 'Low';
  }, []);

  const toggleSort = useCallback((key: SortKey): void => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
      return;
    }
    setSortDir((prev) =>
      prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'
    );
  }, [sortBy]);

  const getSortValue = useCallback(
    (key: SortKey, task: TaskByEmployee): string | number => {
      switch (key) {
        case 'title':
          return task.task_title || '';
        case 'project':
          return task.mattername || task.matterid || '';
        case 'client': {
          if (!task.matterid) return '';
          const clientInfo = clientInfoByMatterId.get(task.matterid);
          if (!clientInfo) return '';
          return clientInfo.clientname || clientInfo.clientid || '';
        }
        case 'leader':
          return task.assignedto
            ? employeeNameById.get(task.assignedto) || ''
            : '';
        case 'status':
          return task.status || '';
        case 'scope':
          return task.task_type || '';
        case 'duedate':
          return task.duedate || '';
        case 'priority': {
          const priority = getPriority(task).toLowerCase();
          return PRIORITY_RANK[priority] ?? PRIORITY_RANK.none;
        }
        default:
          return '';
      }
    },
    [clientInfoByMatterId, employeeNameById, getPriority]
  );

  // Sort filtered list before pagination (matches MattersTable cycle)
  const sortedTasks = useMemo(() => {
    if (!sortBy || !sortDir) return filteredTasks;

    const comparator = (a: TaskByEmployee, b: TaskByEmployee) => {
      const va = getSortValue(sortBy, a);
      const vb = getSortValue(sortBy, b);

      if (sortBy === 'priority') {
        return Number(va) - Number(vb);
      }

      if (sortBy === 'duedate') {
        const da = va ? Date.parse(String(va)) : Number.POSITIVE_INFINITY;
        const db = vb ? Date.parse(String(vb)) : Number.POSITIVE_INFINITY;
        if (!Number.isNaN(da) && !Number.isNaN(db)) {
          return da - db;
        }
      }

      return String(va).localeCompare(String(vb), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    };

    const sorted = [...filteredTasks].sort(comparator);
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [filteredTasks, sortBy, sortDir, getSortValue]);

  const paginatedTasks = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return sortedTasks.slice(start, end);
  }, [sortedTasks, page, pageSize]);

  const SortHeader = ({
    label,
    colKey,
  }: {
    label: string;
    colKey: SortKey;
  }) => (
    <div
      onClick={() => toggleSort(colKey)}
      data-testid={`sort-header-${colKey}`}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span>{label}</span>
      <span>
        {sortBy === colKey && sortDir === 'asc' && (
          <FontAwesomeIcon icon={faSortUp} />
        )}
        {sortBy === colKey && sortDir === 'desc' && (
          <FontAwesomeIcon icon={faSortDown} />
        )}
        {(sortBy !== colKey || sortDir === null) && (
          <FontAwesomeIcon icon={faSort} />
        )}
      </span>
    </div>
  );

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: <SortHeader label="Action Item" colKey="title" />,
        render: (row: TaskByEmployee) => (
          <div
            className="matter-name-cell task-row-clickable"
            onClick={() => handleTaskRowClick(row)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTaskRowClick(row);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div
              className="matter-icon-wrapper"
              style={{ backgroundColor: '#17a2b8' }}
            >
              <FontAwesomeIcon icon={faListCheck} className="matter-type-icon" />
            </div>
            <div className="matter-name-content">
              <div className="matter-name-text">
                {row.task_title || `Action Item ${row.taskid || '—'}`}
              </div>
              <div className="matter-id-text">{row.taskid || '—'}</div>
            </div>
          </div>
        ),
      },
      {
        header: (
          <SortHeader
            label={isEngineeringEnvironment ? 'Projects' : 'Matter'}
            colKey="project"
          />
        ),
        render: (row: TaskByEmployee) => (
          <div className="task-matter-cell">{row.mattername || row.matterid || '-'}</div>
        ),
      },
      {
        header: <SortHeader label="Client" colKey="client" />,
        render: (row: TaskByEmployee) => {
          const clientInfo = row.matterid ? clientInfoByMatterId.get(row.matterid) : null;
          if (clientInfo) {
            return (
              <div className="task-client-cell">
                {clientInfo.clientid ? (
                  <>
                    <strong>{clientInfo.clientid}</strong> - {clientInfo.clientname}
                  </>
                ) : (
                  clientInfo.clientname || '-'
                )}
              </div>
            );
          }
          return <div className="task-client-cell">-</div>;
        },
      },
      {
        header: (
          <SortHeader
            label={
              activeTab === 'tasks-assigned'
                ? 'Person You Assigned To'
                : 'Assigned Leader'
            }
            colKey="leader"
          />
        ),
        render: (row: TaskByEmployee) => {
          const name = row.assignedto ? employeeNameById.get(row.assignedto) || '-' : '-';
          const firstChar = name && name !== '-' ? name.charAt(0).toUpperCase() : '?';
          return (
            <div className="task-leader-cell">
              <div className="task-leader-avatar">
                {firstChar}
              </div>
              <div className="task-leader-info">
                <div className="task-leader-name">{name}</div>
              </div>
            </div>
          );
        },
      },
      {
        header: <SortHeader label="Status" colKey="status" />,
        render: (row: TaskByEmployee) => (
          <StatusBadge status={row.status || 'Pending'} className="task-status-badge" />
        ),
      },
      {
        header: <SortHeader label="Scope" colKey="scope" />,
        render: (row: TaskByEmployee) => (
          <StatusBadge
            status={row.task_type || 'Internal'}
            variant="active"
            className="task-scope-badge"
          />
        ),
      },
      {
        header: <SortHeader label="Due Date" colKey="duedate" />,
        render: (row: TaskByEmployee) => {
          const { date, status, daysLeft } = formatDueDate(row.duedate);
          return (
            <div className="task-due-date-cell">
              <div className="task-due-date">{date}</div>
              {daysLeft !== null && (
                <div className={`task-due-date-status task-due-date-${status}`}>
                  {status === 'overdue'
                    ? `${daysLeft} days overdue`
                    : status === 'today'
                    ? 'Due today'
                    : `${daysLeft} days left`}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: <SortHeader label="Priority" colKey="priority" />,
        render: (row: TaskByEmployee) => {
          const priority = getPriority(row);
          // Determine variant based on priority level
          let variant: 'at-risk' | 'pending' | 'active' = 'active';
          if (priority === 'Urgent' || priority === 'High') {
            variant = 'at-risk';
          } else if (priority === 'Medium') {
            variant = 'pending';
          } else {
            variant = 'active';
          }
          return (
            <StatusBadge
              status={priority}
              variant={variant}
              className="task-priority-badge"
            />
          );
        },
      },
    ],
    [
      employeeNameById,
      activeTab,
      clientInfoByMatterId,
      sortBy,
      sortDir,
      getPriority,
      toggleSort,
    ]
  );

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as 'my-tasks' | 'teams-tasks' | 'tasks-assigned');
    setPage(1);
  };

  const handleCreateActionItem = () => {
    setIsCreateModalOpen(true);
  };

  const handleTaskRowClick = (task: TaskByEmployee) => {
    setSelectedTask(task);
    setIsDetailsPanelOpen(true);
  };

  const handleCloseDetailsPanel = () => {
    setIsDetailsPanelOpen(false);
    setSelectedTask(null);
    
    // Remove taskId from URL when closing panel
    if (taskIdFromUrl) {
      const params = new URLSearchParams(searchParams);
      params.delete('taskId');
      setSearchParams(params, { replace: true });
    }
  };

  const handleEditTask = (updatedTask?: TaskByEmployee) => {
    // Update the selected task with the new data if provided
    if (updatedTask) {
      setSelectedTask(updatedTask);
    }
    // Refetch tasks to ensure the list is up to date
    queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
  };

  const handleDeleteTask = async () => {
    if (selectedTask?.taskid) {
      // TODO: Implement delete functionality
      console.log('Delete task:', selectedTask.taskid);
      handleCloseDetailsPanel();
    }
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleStatusFilterClick = (status: string | null) => {
    // Toggle: if clicking the same card, return to default view (Pending + In Progress)
    setStatusFilter(statusFilter === status ? null : status);
    setPage(1); // Reset to first page when filtering
  };

  const tabs = [
    { id: 'my-tasks', label: 'My Tasks' },
    ...(canViewTeamTasks ? [{ id: 'teams-tasks', label: "Team's Tasks" }] : []),
    { id: 'tasks-assigned', label: 'Tasks Assigned' },
  ];

        return (
    <div className="tasks-page">
      <PageHeader
        title="Action Item"
        subtitle="Manage and track all tasks across all legal matters"
      >
        <CreateButton
          onClick={handleCreateActionItem}
          text="Create Action Item"
          showIcon
          aria-label="Create action item"
        />
      </PageHeader>

      {/* Tabs, Search, and Filters Section - White Card Container */}
      <div className="tasks-filters-container">
        {/* Top Row: Tabs (left), Task Count (middle), Search (right) */}
        <div className="tasks-top-row">
          {/* Tabs on the left */}
          <div className="tasks-tabs-section">
            <Tabs
              tabs={tabs}
              activeTabId={activeTab}
              onTabChange={handleTabChange}
              variant="pills"
            />
            <span className="tasks-summary">
              Showing {paginatedTasks.length} of {filteredTasks.length} tasks
            </span>
          </div>

          {/* Search bar and filter button on the right */}
          <div className="tasks-search-filter-group">
            <SearchBar
              defaultValue={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search tasks or employees..."
              className="tasks-search-bar"
            />
          </div>
        </div>

        {/* Bottom Row: Filter Dropdowns */}
        <div className="tasks-filters">
          <FilterDropdown
            label=""
            options={filterOptions.matters}
            value={matterFilter}
            onChange={(value) => {
              setMatterFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="tasks-filter"
            placeholder={isEngineeringEnvironment ? 'All Projects' : 'All Matters'}
          />
          <FilterDropdown
            label=""
            options={filterOptions.countries}
            value={countryFilter}
            onChange={(value) => {
              setCountryFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="tasks-filter"
            placeholder="All Areas"
          />
          <FilterDropdown
            label=""
            options={filterOptions.scopes}
            value={scopeFilter}
            onChange={(value) => {
              setScopeFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="tasks-filter"
            placeholder="All Scopes"
          />
          <FilterDropdown
            label=""
            options={filterOptions.priorities}
            value={priorityFilter}
            onChange={(value) => {
              setPriorityFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="tasks-filter"
            placeholder="All Priorities"
          />
          <FilterDropdown
            label=""
            options={filterOptions.leaders}
            value={leaderFilter}
            onChange={(value) => {
              setLeaderFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="tasks-filter"
            placeholder="All Leaders"
            searchable
            searchPlaceholder="Search employees..."
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="tasks-summary-cards">
        <div 
          className={`tasks-summary-card ${statusFilter === 'Total' ? 'tasks-summary-card-active' : ''} ${hasHiddenWorkQueueMatches ? 'tasks-summary-card-hint' : ''}`}
          onClick={() => handleStatusFilterClick('Total')}
          data-testid="summary-card-total"
        >
          <div className="tasks-summary-card-icon">
            <FontAwesomeIcon icon={faList} />
          </div>
          <div className="tasks-summary-card-content">
            <div className="tasks-summary-card-label">Total Items</div>
            <div className="tasks-summary-card-value">{summaryCounts.total}</div>
            {hasHiddenWorkQueueMatches && (
              <div className="tasks-summary-card-hint-label">Includes hidden matches</div>
            )}
          </div>
        </div>
        <div 
          className={`tasks-summary-card ${statusFilter === 'In Progress' ? 'tasks-summary-card-active' : ''}`}
          onClick={() => handleStatusFilterClick('In Progress')}
          data-testid="summary-card-in-progress"
        >
          <div className="tasks-summary-card-icon in-progress">
            <FontAwesomeIcon icon={faSpinner} />
          </div>
          <div className="tasks-summary-card-content">
            <div className="tasks-summary-card-label">In Progress</div>
            <div className="tasks-summary-card-value">{summaryCounts.inProgress}</div>
          </div>
        </div>
        <div 
          className={`tasks-summary-card ${statusFilter === 'Overdue' ? 'tasks-summary-card-active' : ''}`}
          onClick={() => handleStatusFilterClick('Overdue')}
          data-testid="summary-card-overdue"
        >
          <div className="tasks-summary-card-icon overdue">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <div className="tasks-summary-card-content">
            <div className="tasks-summary-card-label">Overdue</div>
            <div className="tasks-summary-card-value">{summaryCounts.overdue}</div>
          </div>
        </div>
        <div 
          className={`tasks-summary-card ${statusFilter === 'Completed' ? 'tasks-summary-card-active' : ''} ${hasHiddenWorkQueueMatches && summaryCounts.completed > 0 ? 'tasks-summary-card-hint' : ''}`}
          onClick={() => handleStatusFilterClick('Completed')}
          data-testid="summary-card-completed"
        >
          <div className="tasks-summary-card-icon completed">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="tasks-summary-card-content">
            <div className="tasks-summary-card-label">Completed</div>
            <div className="tasks-summary-card-value">{summaryCounts.completed}</div>
            {hasHiddenWorkQueueMatches && summaryCounts.completed > 0 && (
              <div className="tasks-summary-card-hint-label">Click to view</div>
            )}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="tasks-table-section">
        <div className={getDensityClass(pageSize)}>
          <Table
            columns={columns}
            data={paginatedTasks}
            emptyMessage={
              hasHiddenWorkQueueMatches
                ? summaryCounts.completed > 0
                  ? `No active tasks match these filters. ${summaryCounts.completed} completed ${summaryCounts.completed === 1 ? 'task is' : 'tasks are'} hidden by the default work queue.`
                  : `No active tasks match these filters. ${filteredTasksForSummary.length} matching ${filteredTasksForSummary.length === 1 ? 'task is' : 'tasks are'} hidden by the default work queue.`
                : 'No tasks found'
            }
            emptyAction={
              hasHiddenWorkQueueMatches ? (
                <div className="tasks-empty-actions" data-testid="work-queue-empty-actions">
                  <button
                    type="button"
                    className="tasks-empty-action-button"
                    onClick={() => handleStatusFilterClick('Total')}
                  >
                    View Total Items
                  </button>
                  {summaryCounts.completed > 0 && (
                    <button
                      type="button"
                      className="tasks-empty-action-button tasks-empty-action-button-secondary"
                      onClick={() => handleStatusFilterClick('Completed')}
                    >
                      View Completed
                    </button>
                  )}
                </div>
              ) : undefined
            }
            rowKey="taskid"
            pagination={{
              currPage: page,
              onPageChange: setPage,
              pageLimit,
              pageSize,
              onPageSizeChange: setPageSize,
              totalItems: filteredTasks.length,
              itemLabel: 'tasks',
            }}
          />
        </div>
      </div>

      {/* Create Tasks Modal Overlay */}
      {isCreateModalOpen && (
        <CreateTasksPage onClose={handleCloseCreateModal} />
      )}

      {/* Action Item Details Panel */}
      <ActionItemDetailsPanel
        task={selectedTask}
        onClose={handleCloseDetailsPanel}
        isOpen={isDetailsPanelOpen}
        onEdit={handleEditTask}
        onDelete={handleDeleteTask}
      />
    </div>
  );
};

export default TasksPage;
